import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { HttpStatusCode } from "axios";
import { API_STATUS } from "../sliceUtil";
import { _get, _post } from "../../../api/apiClient";
import { CustomError } from "../../../api/CustomError";
import store from "../../../app/store";
import { setLoaderFalse, setLoaderTrue } from "../../../Redux/loaderSlice";
import { CustomSuccess } from "../../../api/CustomSuccess";
import { toast } from "react-toastify";

const initialStripeState = {
  subscriptionProductsList: null,
  forceLogout: false,
  unauthorizedForceLogout: false,
  acknowledgement: null,
};

export const _orgClientId = () => {
  const state = store.getState();
  return state?.authReducer?.userData?.client_Id;
};

export const fetchSubscriptionProducts = createAsyncThunk(
  "/stripe/products",
  async ({ disableLoader }, thunkAPI) => {
    try {
      if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Invoice details fetched.",
        errorMessage: "Something went wrong.",
      };
      const response = await _get(`/payment/product`, {});

      if (response?.data?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const { data, error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      thunkAPI.dispatch(setLoaderFalse());
      return {
        data,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err?.error);
    }
  },
);

export const createPaymentIntent = createAsyncThunk(
  "/stripe/payment-intent",
  async ({ plan, selectedPlan, disableLoader = false }, thunkAPI) => {
    try {
      if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());

      const config = {
        successMessage: "Payment Sucessfull",
        errorMessage: "Something went wrong.",
        disableToast: true,
      };

      const response = await _post(
        `${_orgClientId()}/payment/intent`,
        {
          productId: plan?.id,
          priceId: selectedPlan?.id,
        },
        config,
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw new CustomError(response, config);
      }

      const { data } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });

      globalThis.location.href = data?.url;
      thunkAPI.dispatch(setLoaderFalse());
      return null;
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());

      const errorMessage =
        err?.error ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong";

      toast.error("Something went wrong");

      return thunkAPI.rejectWithValue(err.response.data);
    }
  },
);

export const subscribeTrialPeriod = createAsyncThunk(
  "/stripe/subscribe-trial-period",
  async ({ plan, disableLoader = false }, thunkAPI) => {
    try {
      if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());

      const config = {
        successMessage: "Subscribed Sucessfull",
        errorMessage: "Something went wrong.",
        disableToast: true,
      };

      const response = await _post(
        `${_orgClientId()}/payment/subscription`,
        {
          productId: plan?.id,
          priceId: plan?.price?.id,
        },
        config,
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw new CustomError(response, config);
      }

      const { data } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });

      thunkAPI.dispatch(
        setAcknowledgement({
          trial_start: data?.trial_start,
          trial_end: data?.trial_end,
        }),
      );
      thunkAPI.dispatch(setForceLogout(true));
      thunkAPI.dispatch(setLoaderFalse());
      return null;
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err.response.data);
    }
  },
);

export const fetchInvoiceDetails = createAsyncThunk(
  "/stripe/invoice",
  async ({ disableLoader }, thunkAPI) => {
    try {
      if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Invoice details fetched.",
        errorMessage: "Something went wrong.",
      };
      const response = await _get(`${_orgClientId()}/payment/invoice`, {});
      if (response?.data?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      thunkAPI.dispatch(setLoaderFalse());
      return response;
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err?.error);
    }
  },
);

export const generateCustomerId = createAsyncThunk(
  "/stripe/createPaymentId",
  async ({ disableLoader }, thunkAPI) => {
    try {
      if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Customer id created.",
        errorMessage: "Something went wrong.",
      };
      thunkAPI.dispatch(setLoaderTrue());
      const response = await _get(`${_orgClientId()}/payment/subscription`, {});
      if (response?.data?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      thunkAPI.dispatch(setLoaderFalse());
      return response;
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err?.error);
    }
  },
);

export const stripeSlice = createSlice({
  name: "stripe",
  initialState: initialStripeState,
  reducers: {
    setAcknowledgement: (state, action) => {
      state.acknowledgement = action.payload;
    },
    setForceLogout: (state, action) => {
      state.forceLogout = action.payload;
    },
    setUnauthorizedForceLogout: (state, action) => {
      state.unauthorizedForceLogout = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionProducts.pending, (_state, action) => ({
        ..._state,
        subscriptionProductsList: {
          isLoading: API_STATUS.STATUS_LOADING,
          data: null,
        },
      }))
      .addCase(fetchSubscriptionProducts.fulfilled, (_state, action) => {
        return {
          ..._state,
          subscriptionProductsList: {
            ...action.payload,
            isLoading: API_STATUS.STATUS_SUCCESS,
          },
        };
      })
      .addCase(fetchSubscriptionProducts.rejected, (_state, action) => {
        return {
          subscriptionProductsList: {
            ..._state,
            ...action.payload,
            isLoading: API_STATUS.STATUS_FAILURE,
            message: action.error.message || "Error while creating user",
          },
        };
      })
      .addCase(fetchInvoiceDetails.pending, (_state, action) => ({
        ..._state,
        isLoading: API_STATUS.STATUS_LOADING,
      }))
      .addCase(fetchInvoiceDetails.fulfilled, (_state, action) => {
        return {
          ..._state,
          isLoading: API_STATUS.STATUS_SUCCESS,
          invoiceDetails: action.payload,
        };
      })
      .addCase(fetchInvoiceDetails.rejected, (_state, action) => {
        return {
          ..._state,
          isLoading: API_STATUS.STATUS_FAILURE,
          message: action.error.message || "Error while creating user",
          invoiceDetails: [],
        };
      });
  },
});

export const {
  setAcknowledgement,
  setForceLogout,
  setUnauthorizedForceLogout,
} = stripeSlice.actions;

export default stripeSlice.reducer;
